import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { format } from "date-fns";
import ReactTable from "components/lib/Global/ReactTable";
import Sidebar from "components/Applications/Sidebar";
import HeroSection from "components/Applications/HeroSection";
import OrgAdminDashboardWrapper from "components/lib/Global/OrgAdminDashboardWrapper";
import SearchInput from "components/lib/Shared/SearchInput";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { getCompaniesAsync } from "state/slices/companies";
import SubRowAsync from "./SubRow";
import { Constants } from "config/constants";
import Checkbox from "components/lib/Shared/Checkbox";
import GoBackToHomeBtn from "../GoBackToHomeBtn";
import { getCompanyNameWithSuffix } from "utils/company";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import { getHsEventsFromEvelopes, getSigners } from "utils/auditTrial";
import esignatureService from "services/esignature";
import { useToast } from "hooks/useToast";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { getTransactionListAsync } from "state/slices/transaction";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import { getCompaniesTransactionsListAsync } from "state/slices/companiesTransactions";

const statuses = [
  { value: "Signature", label: "Signature" },
  { value: "Signature Corrections", label: "Signature Corrections" },
  { value: "Signature Deferred", label: "Signature Deferred" },
];

export default function Signature() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();
  const history = useHistory();
  const { toast } = useToast(5000);
  const role = Cookies.get("role");
  const { getUserStatus } = useSelector((state) => state.authSlice);
  const { getTransactionList, transactionList } = useSelector(
    (state) => state.transactionSlice
  );
  const { getCompaniesTransactionsList, companiesTransactionsList } =
    useSelector((state) => state.companiesTransactionsSlice);
  const { dashboardRoles } = useSelector(
    (state) => state.companyDashboardSlice
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(() =>
    statuses.map((status) => status.value)
  );
  const { orgAdmin } = useSelector((state) => state.roleswitchSlice);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (isAuthenticated) {
      if (debouncedSearchTerm) setCurrentPage(1);
      const queryParams = new URLSearchParams(location.search);
      const status = queryParams.getAll("status");
      setFilters(queryParams.getAll("status"));
      dispatch(
        getCompaniesTransactionsListAsync({
          queryParams: {
            limit: rowsPerPage,
            skip: (currentPage - 1) * rowsPerPage,
            search: debouncedSearchTerm,
            status: status.length > 0 ? status : null,
            order: "asc",
            orgAdmin: orgAdmin ? orgAdmin : "",
          },
        })
      );
    }
  }, [
    isAuthenticated,
    rowsPerPage,
    currentPage,
    debouncedSearchTerm,
    statuses,
    location.search,
  ]);

  const currentUser = useSelector((state) => state.userSlice);

  const refreshTableData = async () => {
    dispatch(
      getCompaniesTransactionsListAsync({
        queryParams: {
          limit: rowsPerPage,
          skip: (currentPage - 1) * rowsPerPage,
          search: debouncedSearchTerm,
          status: [
            "Signature",
            "Signature Corrections",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE,
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE,
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_SIGNATURE,
          ],
          order: "asc",
          orgAdmin: orgAdmin ? orgAdmin : "",
        },
      })
    );
  };

  // this function will render our row sub components
  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => {
      return (
        <SubRowAsync
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          refreshTableData={refreshTableData}
          isViewMode={role === Constants.COOKIE_ROLES.ORG_ADMIN}
        />
      );
    },
    []
  );
  const handleEmailReminder = async ({ envelopes, companyName }) => {
    const incorporationHsEvents = getHsEventsFromEvelopes(envelopes);
    if (incorporationHsEvents.length > 0) {
      const signers = await getSigners(incorporationHsEvents);
      const signersWhoHaveNotSigned = signers.filter(
        (signer) => signer.status_code === "awaiting_signature"
      );

      if (signersWhoHaveNotSigned.length > 0) {
        const response = await esignatureService.createEmailReminder({
          companyName,
          signers: signersWhoHaveNotSigned,
        });
        if (response.statusText === "OK") {
          toast("success", "Reminder email sent successfully!");
        } else {
          toast("error", "Failed to send email reminder");
        }
      }
    }
  };

  const handleVoid = async ({ companyId, envelopes }) => {
    if (
      window.confirm(
        `Are you sure you want to void this eSignature request. This will return the application to the previous step - Submission?`
      )
    ) {
      const hsEvents = getHsEventsFromEvelopes(envelopes);
      const signatureRequestSent = hsEvents.filter((request) => {
        return request.event.event_type === "signature_request_sent";
      });
      let signatureRequestIds = signatureRequestSent.map(
        (request) => request?.signature_request.signature_request_id
      );
      const signers = await getSigners(hsEvents);

      const response = await esignatureService.voidCompany({
        companyId,
        signatureRequestIds,
        signers,
      });
      refreshTableData();
    }
  };

  if (!currentUser?._id || getUserStatus.status === "loading") {
    return (
      <OrgAdminDashboardWrapper>
        <div className="flex justify-center mx-auto mt-10 text-white">
          <SpinnerIcon className="text-gray-400" />
        </div>
      </OrgAdminDashboardWrapper>
    );
  }

  const columns = [
    {
      Header: "Batch No.",
      accessor: "BatchNo",
      Cell: ({ row }) => row.original?.batchNo || "-",
    },
    {
      Header: "Name.",
      accessor: "companyName1",
      Cell: ({ row }) => {
        if (row.original?.companyInfo) {
          return getCompanyNameWithSuffix(row.original.companyInfo);
        } else {
          return getCompanyNameWithSuffix(row.original);
        }
      },
    },
    {
      Header: "Application Type",
      accessor: "applicationType",

      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return row.original?.applicationType;
        } else {
          if (row.original?.transferYourBusinessInitiator) {
            return "Transfer Your Business";
          }
          return "Incorporation";
        }
      },
    },
    {
      Header: "Change Type",
      accessor: "changeType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        return (
          capitalize(original?.changeType) || original?.companyInfo?.type || "-"
        );
      },
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          let transactionType =
            str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          return transactionType;
        };

        return capitalize(original?.transactionType) || "Automatic";
      },
    },
    {
      Header: "Tnx No.",
      accessor: "TnxNo",
      Cell: ({ row: { original } }) => {
        return (
          original?.trxRef || original?.companyInfo?.registrationNumber || "-"
        );
      },
    },
    {
      Header: "Date",
      Cell: ({ row: { original } }) => {
        const date = original?.createdAt;
        return date ? format(new Date(date), "yyyy/MM/dd") : "N/A";
      },
    },
    {
      Header: "Status",
      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return getMaintainYourCompanyReviewStatus(row.original?.status);
        } else {
          return row.original?.status || row.original?.status;
        }
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        if (row.original?.envelopes?.length > 0) {
          return (
            <TableActionMenu
              row={row}
              showViewBtn={true}
              showFileDownloadBtn={true}
              showVoid={
                dashboardRoles?.some(
                  (role) =>
                    role.name ===
                    Roles_Permissions_Constants.DASHBOARD_ROLES.APPROVER
                ) ||
                [
                  Constants.COOKIE_ROLES.SUPER_ADMIN,
                  Constants.COOKIE_ROLES.ORG_ADMIN,
                  Constants.COOKIE_ROLES.BRS_OPS,
                ].includes(role)
              }
              hideDeleteBtn={true}
              hideEditBtn={true}
              renderViewAsSubRow
              showEmailReminder={true}
              handleVoid={() =>
                handleVoid({
                  companyId: row.original._id,
                  envelopes: row.original?.envelopes,
                })
              }
              handleEmailReminder={() =>
                handleEmailReminder({
                  envelopes: row.original?.envelopes,
                  companyName: row.original?.companyName1WithSuffix,
                })
              }
              documentsToDownload={
                row.original?.fileUploads?.formCR1 ||
                row.original?.fileUploads?.formCR2
                  ? row.original.fileUploads
                  : row.original.preSignedBusinessDocuments
              }
            />
          );
        } else {
          return (
            <button
              type="button"
              {...row?.getToggleRowExpandedProps()}
              className={`text-gray-900 group flex rounded-md items-center w-full px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500`}
            >
              {row.isExpanded ? "Close" : "View"}
            </button>
          );
        }
      },
    },
  ];

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  const handleFilterChange = (e) => {
    const params = new URLSearchParams();
    if (e.target.checked) {
      const tempFilters = [...filters, e.target.value];
      tempFilters.forEach((status) => params.append("status", status));
      history.push({ search: params.toString() });
      setFilters([...filters, e.target.value]);
    } else {
      const tempFilters = filters.filter((filter) => filter !== e.target.value);
      tempFilters.forEach((status) => params.append("status", status));
      history.push({ search: params.toString() });
      setFilters(tempFilters);
    }
    setCurrentPage(1);
  };

  return (
    <OrgAdminDashboardWrapper>
      <div className="grid gap-4 py-10 mx-auto">
        <HeroSection />
        <GoBackToHomeBtn />
        <div className="grid grid-cols-6 border-t border-gray">
          <div className="col-span-6 pr-8 md:col-span-1">
            <Sidebar />
          </div>
          <div className="col-span-6 pt-8 md:border-l md:col-span-5 md:pl-8 border-gray">
            <div className="mb-6">
              <h1 className="text-title5">Pending Applications</h1>
            </div>
            <div className="flex items-center justify-between my-2">
              <div className="flex items-center space-x-4">
                <h2 className="font-semibold">Filter by:</h2>
                <div className="flex space-x-2 my-2">
                  {statuses.map((status) => (
                    <Checkbox
                      key={status.value}
                      name={status.label}
                      label={status.label}
                      checked={filters.includes(status.value)}
                      onChange={handleFilterChange}
                    />
                  ))}
                </div>
              </div>
              <SearchInput value={searchTerm} handleOnChange={handleSearch} />
            </div>
            {companiesTransactionsList.items.length > 0 ? (
              <ReactTable
                columns={columns}
                data={companiesTransactionsList.items}
                totalRows={companiesTransactionsList.totalCount}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                setCurrentPage={setCurrentPage}
                paginate
                renderRowSubComponent={renderRowSubComponent}
                isLoading={getCompaniesTransactionsList.status === "loading"}
              />
            ) : (
              <div className="flex items-center justify-center mt-10">
                {getCompaniesTransactionsList.status === "loading" ? (
                  <SpinnerIcon className="text-gray-400" />
                ) : (
                  <p className="text-2xl text-center text-primary">
                    No applications available.
                  </p>
                )}
              </div>
            )}
            {!getCompaniesTransactionsList.status === "loading" && (
              <div className="flex items-center justify-center mt-10">
                <p className="text-2xl text-center text-primary">
                  No applications available.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </OrgAdminDashboardWrapper>
  );
}
